/**
 * Lazy beam calculation report: builds self-contained HTML and opens a new tab + print dialog.
 */

import { eurocodeDesignChecksTitle, eurocodeNaNotePlain } from './eurocodeDesignChecksChrome';

function escapeHtml(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const fmt1 = (x) => (x != null && !Number.isNaN(Number(x)) ? Number(x).toFixed(1) : '—');

/** Human-readable magnitude with unit for beam loads (report). */
function formatLoadMagnitudePhrase(load) {
  const raw = load.magnitude;
  const t = load.type === 'udl' ? 'line' : load.type;
  if (raw == null || raw === '') return 'magnitude —';
  const n = Math.abs(parseFloat(String(raw).replace(',', '.')));
  if (Number.isNaN(n)) return `magnitude ${String(raw)}`;
  const formatted = Number.isInteger(n) ? String(n) : String(Number(n.toFixed(4))).replace(/\.?0+$/, '');
  if (t === 'line') return `magnitude ${formatted} kN/m`;
  if (t === 'point') return `magnitude ${formatted} kN`;
  if (t === 'moment') return `magnitude ${formatted} kNm`;
  return `magnitude ${formatted}`;
}

function formatLoadActionPhrase(load) {
  const a = load.loadAction;
  if (a == null || a === '') return 'action —';
  const s = String(a).replace(/_/g, ' ');
  return `action ${s}`;
}

/** One-line description for geometry & loads table. */
function formatLoadRowDescription(load) {
  const t = load.type === 'udl' ? 'line' : load.type;
  const typeLabel =
    t === 'line' ? 'Line load (UDL)' : t === 'point' ? 'Point load' : t === 'moment' ? 'Moment' : 'Load';
  const parts = [typeLabel, formatLoadActionPhrase(load), formatLoadMagnitudePhrase(load)];
  if (t === 'line') {
    parts.push(
      `from ${load.startPosition ?? '—'} m to ${load.endPosition ?? '—'} m`,
      `coordinate system: ${load.coordinateSystem || 'global'}`,
    );
  } else {
    parts.push(`position ${load.position ?? '—'} m (along span)`);
  }
  return parts.join(', ');
}

function splitEc3CaseLabel(fullLabel) {
  if (!fullLabel) return { detail: '', caseTag: '' };
  const sep = ' — ';
  const i = fullLabel.lastIndexOf(sep);
  if (i < 0) return { detail: fullLabel.trim(), caseTag: '' };
  return { detail: fullLabel.slice(0, i).trim(), caseTag: fullLabel.slice(i + sep.length).trim() };
}

function utilCellClass(u) {
  if (u == null || Number.isNaN(Number(u))) return '';
  const n = Math.min(Number(u), 1);
  if (n <= 0.7) return 'util-low';
  if (n <= 0.9) return 'util-mid';
  return 'util-high';
}

function formatUtilPct(u) {
  if (u == null || Number.isNaN(Number(u))) return '—';
  const pct = Number(u) * 100;
  return pct > 10000 ? `${pct.toExponential(2)}%` : `${pct.toFixed(1)}%`;
}

/** Plain-text / HTML rows for EC2 (mirrors ResultsDisplay ec2Rows). */
function buildEc2PrintRows(ec2) {
  if (!ec2 || ec2.error) return [];
  const rows = [];
  const push = (label, clause, util, ok, extra = '', hint = '') => {
    rows.push({
      label,
      clause: clause || '',
      utilization: util,
      ok: !!ok,
      severity: 'success',
      extra,
      hint,
    });
  };
  if (ec2.bending) {
    push(
      'Bending',
      ec2.bending.clause,
      ec2.bending.utilization,
      ec2.bending.ok,
      `|M_Ed|=${ec2.bending.M_Ed_kNm} kNm, M_Rd=${ec2.bending.M_Rd_kNm} kNm`,
    );
  }
  if (ec2.ductility_x_d) {
    const lim = ec2.ductility_x_d.limit || 0.45;
    push(
      'Ductility x/d',
      ec2.ductility_x_d.clause,
      ec2.ductility_x_d.x_over_d / lim,
      ec2.ductility_x_d.ok,
      `x/d=${ec2.ductility_x_d.x_over_d}, limit=${lim}`,
      ec2.ductility_x_d.hint || '',
    );
  }
  if (ec2.reinforcement_limits) {
    const umin = ec2.reinforcement_limits.A_s_m2 / Math.max(ec2.reinforcement_limits.A_s_min_m2, 1e-12);
    push('A_s ≥ A_s,min', ec2.reinforcement_limits.clause, umin, ec2.reinforcement_limits.ok_min, '');
    const umax = ec2.reinforcement_limits.A_s_m2 / Math.max(ec2.reinforcement_limits.A_s_max_m2, 1e-12);
    push('A_s ≤ A_s,max', ec2.reinforcement_limits.clause, umax, ec2.reinforcement_limits.ok_max, '');
  }
  if (ec2.shear_V_Rd_c) {
    push('Shear V_Rd,c', ec2.shear_V_Rd_c.clause, ec2.shear_V_Rd_c.utilization, ec2.shear_V_Rd_c.ok, '');
  }
  if (ec2.shear_with_links) {
    const links = ec2.shear_with_links.links_required
      ? `A_sw/s req.=${ec2.shear_with_links.A_sw_over_s_required_m2_per_m} m²/m`
      : '';
    push(
      'Shear strut (V_Rd,max)',
      ec2.shear_with_links.clause,
      ec2.shear_with_links.utilization_strut,
      ec2.shear_with_links.ok,
      links,
    );
  }
  if (ec2.sls_stress_concrete) {
    const u = (ec2.sls_stress_concrete.sigma_c_MPa || 0) / Math.max(ec2.sls_stress_concrete.limit_MPa || 1, 1e-6);
    push('SLS concrete stress', ec2.sls_stress_concrete.clause || '§7.2', u, ec2.sls_stress_concrete.ok, '');
  }
  if (ec2.sls_stress_steel) {
    const u = (ec2.sls_stress_steel.sigma_s_MPa || 0) / Math.max(ec2.sls_stress_steel.limit_MPa || 1, 1e-6);
    const sig = ec2.sls_stress_steel.sigma_s_MPa;
    const lims = ec2.sls_stress_steel.limit_MPa;
    const mqp = ec2.sls_stress_steel.M_qp_kNm;
    let extra = '';
    if (sig != null) extra = `${sig.toFixed(1)} / ${lims?.toFixed(0)} MPa`;
    if (mqp != null) extra += ` (M_qp≈${mqp} kNm)`;
    push('SLS steel stress', ec2.sls_stress_steel.clause || '§7.2', u, ec2.sls_stress_steel.ok, extra, '');
  }
  if (ec2.sls_crack_width) {
    const u = (ec2.sls_crack_width.w_k_mm || 0) / Math.max(ec2.sls_crack_width.w_lim_mm || 0.4, 1e-6);
    push('Crack width w_k', ec2.sls_crack_width.clause, u, ec2.sls_crack_width.ok, '');
  }
  if (ec2.sls_deflection) {
    push('SLS deflection', ec2.sls_deflection.clause, ec2.sls_deflection.utilization, ec2.sls_deflection.ok, '');
  }
  return rows;
}

function buildEc3PrintRows(ec3, ec3sls) {
  if (!ec3 || ec3.error) return [];
  const rows = [];
  for (const c of ec3.checks || []) {
    if (c.id === 'classification' && c.ok === true && !c.message) continue;
    const isClassWarning = c.id === 'classification' && c.message;
    const { caseTag } = splitEc3CaseLabel(c.label || '');
    const caseSuffix = caseTag ? ` (${caseTag})` : '';

    let label = c.label || c.id;
    let extra = '';
    if (c.message) extra = c.message;
    else if (c.class != null) extra = `Class ${c.class}`;
    else if (c.V_Rd_kN != null) extra = `V_Rd=${fmt1(c.V_Rd_kN)} kN`;
    else if (c.M_Rd_kNm != null) extra = `M_Rd=${fmt1(c.M_Rd_kNm)} kNm`;
    else extra = '';

    let hintParts = [];
    if (c.id === 'bending') {
      label = `Bending capacity — EN 1993-1-1 §6.2${caseSuffix}`;
      if (ec3.class4_effective) {
        hintParts.push(
          `Class 4 effective: W_eff,el=${ec3.class4_effective.W_eff_mm3} mm³, ρ_f=${ec3.class4_effective.rho_flange}, ρ_w=${ec3.class4_effective.rho_web}`,
        );
      }
      const med = c.M_Ed_kNm;
      const mrd = c.M_Rd_kNm;
      if (c.message && (mrd == null || Number.isNaN(Number(mrd)))) extra = c.message;
      else extra = `|M_Ed|=${fmt1(med)} / M_Rd=${fmt1(mrd)} kNm`;
    } else if (c.id === 'shear') {
      label = `Shear capacity — EN 1993-1-1 §6.2.6 & EN 1993-1-5 §5${caseSuffix}`;
      extra = `|V_Ed|=${fmt1(c.V_Ed_kN)} / V_Rd=${fmt1(c.V_Rd_kN)} kN`;
      if (c.V_pl_Rd_kN != null && c.V_bw_Rd_kN != null) {
        hintParts.push(
          `V_pl,Rd=${fmt1(c.V_pl_Rd_kN)} kN, V_bw,Rd=${fmt1(c.V_bw_Rd_kN)} kN, V_Rd=${fmt1(c.V_Rd_kN)} kN`,
        );
      }
      if (c.av_note) hintParts.push(`Shear area: ${c.av_note}`);
      if (c.shear_buckling_note) hintParts.push(`Shear buckling (web): ${c.shear_buckling_note}`);
    } else if (c.id === 'ltb') {
      label = `Lateral-torsional buckling — EN 1993-1-1 §6.3.2${caseSuffix}`;
      if (c.M_cr_kNm != null) {
        hintParts.push(
          `M_cr≈${fmt1(c.M_cr_kNm)} kNm, λ_LT≈${fmt1(c.lambda_LT)}, χ_LT≈${fmt1(c.chi_LT)}, M_b,Rd≈${fmt1(c.M_b_Rd_kNm)} kNm`,
        );
      }
      if (c.M_b_Rd_kNm != null && c.M_Ed_kNm != null) {
        extra = `|M_Ed|=${fmt1(c.M_Ed_kNm)} / M_b,Rd=${fmt1(c.M_b_Rd_kNm)} kNm`;
      }
    } else if (c.id === 'mv_interaction') {
      label = `M–V interaction (plastic) — EN 1993-1-1 §6.2.8${caseSuffix}`;
      extra = `|M_Ed|=${fmt1(c.M_Ed_kNm)} / M_y,V,Rd=${fmt1(c.M_y_V_Rd_kNm)} kNm`;
    } else if (c.id === 'mv_elastic_von_mises') {
      const isCombinedTorsion =
        Boolean(c.elastic_torsion_screening) ||
        (c.sigma_omega_MPa != null && Number(c.sigma_omega_MPa) > 1e-6) ||
        (c.tau_t_MPa != null && Number(c.tau_t_MPa) > 1e-6);
      label = isCombinedTorsion
        ? `Combined bending+shear+torsion (von Mises) — EN 1993-1-1 §6.2.1(5) / §6.2.7${caseSuffix}`
        : `M–V elastic (von Mises) — EN 1993-1-1 §6.2.1(5)${caseSuffix}`;
      if (c.screening_note) hintParts.push(String(c.screening_note));
      if (c.von_Mises_MPa != null) {
        if (isCombinedTorsion && c.sigma_x_bending_MPa != null) {
          hintParts.push(
            `σ_x,bend≈${fmt1(c.sigma_x_bending_MPa)} σ_ω≈${fmt1(c.sigma_omega_MPa)} → σ_x≈${fmt1(c.sigma_x_MPa)} MPa; τ_V≈${fmt1(c.tau_V_MPa)} τ_t≈${fmt1(c.tau_t_MPa)} → τ≈${fmt1(c.tau_MPa)} MPa; √(σ²+3τ²)≈${fmt1(c.von_Mises_MPa)} (f_y/γ_M0≈${fmt1(c.limit_MPa)})`,
          );
          if (c.W_t_est_mm3 != null) hintParts.push(`W_t,est≈${fmt1(c.W_t_est_mm3)} mm³`);
        } else {
          hintParts.push(
            `σ_x≈${fmt1(c.sigma_x_MPa)} MPa, τ≈${fmt1(c.tau_MPa)} MPa, √(σ²+3τ²)≈${fmt1(c.von_Mises_MPa)} MPa (limit f_y/γ_M0≈${fmt1(c.limit_MPa)} MPa)`,
          );
        }
      }
      extra = `σ_vm=${fmt1(c.von_Mises_MPa)} / f_lim=${fmt1(c.limit_MPa)} MPa`;
      if (isCombinedTorsion && c.T_t_Ed_kNm != null && c.B_Ed_kNm2 != null) {
        extra += ` · |T_t,Ed|=${fmt1(c.T_t_Ed_kNm)} kNm |B_Ed|=${fmt1(c.B_Ed_kNm2)} kNm²`;
      }
    } else if (c.id === 'torsion_bending_627') {
      label = `Bending + torsion — EN 1993-1-1 §6.2.7${caseSuffix}`;
      if (c.message) hintParts.push(String(c.message));
      extra = [c.T_t_Ed_kNm != null ? `|T_t,Ed|=${fmt1(c.T_t_Ed_kNm)} kNm` : '', c.B_Ed_kNm2 != null ? `|B_Ed|=${fmt1(c.B_Ed_kNm2)} kNm²` : '']
        .filter(Boolean)
        .join(' · ');
    } else if (c.id === 'ltb_scope_elastic_twist') {
      label = `LTB / elastic M_cr scope — torsional rotation screening${caseSuffix}`;
      if (c.clause) hintParts.push(String(c.clause));
      hintParts.push(
        'Combined bending+torsion at the section appears in the von Mises / §6.2.7 row. The LTB / M_cr check does not use this first-order twist. Imperfection-based LTB assumes small rotations; above the screening limit, general-method assumptions may not apply — consider GMNIA or specialist stability if twist governs.',
      );
      extra = c.message ? String(c.message) : '';
    }

    const hint = hintParts.join(' · ');
    rows.push({
      label,
      clause: c.clause || '',
      utilization: c.utilization != null ? c.utilization : c.ok === false ? 1 : 0,
      ok: c.ok !== false,
      severity: c.severity === 'warning' || isClassWarning ? 'warning' : c.ok === false ? 'error' : 'success',
      extra,
      hint,
    });
  }
  if (ec3sls && !ec3sls.error) {
    rows.push({
      label: ec3sls.label || 'Deflection',
      clause: ec3sls.clause || '',
      utilization: ec3sls.utilization,
      ok: ec3sls.ok,
      severity: ec3sls.ok ? 'success' : 'error',
      extra: `w_Ed=${fmt1(ec3sls.deflection_mm)} / w_Rd=${fmt1(ec3sls.limit_mm)} mm`,
      hint: 'SLS deflection',
    });
  }
  return rows;
}

function buildEc5PrintRows(ec5, ec5sls) {
  if (!ec5 || ec5.error) return [];
  const rows = (ec5.checks || []).map((c) => ({
    label: c.label || c.id,
    clause: c.clause || '',
    utilization: c.utilization != null ? c.utilization : 0,
    ok: c.ok !== false,
    severity: 'success',
    extra:
      c.M_Ed_kNm != null
        ? `M_Ed=${c.M_Ed_kNm} kNm`
        : c.tau_d_MPa != null
          ? `τ_d=${c.tau_d_MPa} MPa`
          : '',
    hint: '',
  }));
  if (ec5sls && !ec5sls.error) {
    rows.push({
      label: ec5sls.label || 'Deflection',
      clause: ec5sls.clause || '',
      utilization: Math.max(ec5sls.utilization_net || 0, ec5sls.utilization_appearance || 0),
      ok: ec5sls.ok,
      severity: ec5sls.ok ? 'success' : 'error',
      extra: `w_fin=${ec5sls.w_fin_mm} mm`,
      hint: '',
    });
  }
  return rows;
}

function renderDesignCheckRow(r) {
  const status =
    r.severity === 'warning'
      ? '<span class="status warn">Warning</span>'
      : r.ok
        ? '<span class="status ok">OK</span>'
        : '<span class="status fail">FAIL</span>';
  const util = r.utilization != null && !Number.isNaN(r.utilization) ? formatUtilPct(r.utilization) : '—';
  const uclass = utilCellClass(r.utilization);
  const hintCell = r.hint ? `<td class="hint">${escapeHtml(r.hint)}</td>` : '<td class="hint"></td>';
  return `<tr>
    <td>${escapeHtml(r.label)}</td>
    <td class="small">${escapeHtml(r.clause)}</td>
    <td>${status}</td>
    <td class="extra">${escapeHtml(r.extra)}</td>
    <td class="util ${uclass}">${escapeHtml(util)}</td>
    ${hintCell}
  </tr>`;
}

function buildDesignChecksHtml(designChecks) {
  if (!designChecks || Object.keys(designChecks).length === 0) {
    return '<p class="muted">No design checks returned.</p>';
  }
  const dcHeading = eurocodeDesignChecksTitle(designChecks);
  let html = `<section class="block"><h2>1. ${escapeHtml(dcHeading)}</h2>`;
  const naFi = designChecks.eurocode_na || designChecks.national_annex_finland;
  const naPlain = eurocodeNaNotePlain(designChecks, naFi);
  if (naPlain) {
    html += `<p class="na-note">${escapeHtml(naPlain)}</p>`;
  }
  const panels = [
    { key: 'ec2', title: 'EN 1992-1-1 (Concrete)', rows: buildEc2PrintRows(designChecks.ec2), err: designChecks.ec2?.error },
    { key: 'ec3', title: 'EN 1993-1-1 (Steel)', rows: buildEc3PrintRows(designChecks.ec3, designChecks.ec3_sls), err: designChecks.ec3?.error },
    { key: 'ec5', title: 'EN 1995-1-1 (Timber)', rows: buildEc5PrintRows(designChecks.ec5, designChecks.ec5_sls), err: designChecks.ec5?.error },
  ];
  for (const p of panels) {
    if (!designChecks[p.key] && !p.err) continue;
    const overall = designChecks[p.key]?.overall_ok;
    const chip =
      overall === false ? '<span class="status fail">FAIL</span>' : overall === true ? '<span class="status ok">OK</span>' : '';
    html += `<h3>${escapeHtml(p.title)} ${chip}</h3>`;
    if (p.err) {
      html += `<p class="err">${escapeHtml(String(p.err))}</p>`;
      continue;
    }
    if (!p.rows.length) {
      html += '<p class="muted">No checks for this standard.</p>';
      continue;
    }
    html += `<table class="checks">
      <thead><tr><th>Check</th><th>Clause</th><th>Status</th><th>Detail</th><th>Util.</th><th>Notes</th></tr></thead>
      <tbody>${p.rows.map(renderDesignCheckRow).join('')}</tbody>
    </table>`;
  }
  html += '</section>';
  return html;
}

function sectionTitle(name) {
  return name || '—';
}

function buildCommonInputsTable(spans, ao, designChecks) {
  let rows = '';
  const n = spans.length;
  spans.forEach((span, si) => {
    const lt =
      span.leftTorsionSupport !== undefined ? span.leftTorsionSupport !== false : span.leftLtbSupport !== false;
    const rt =
      span.rightTorsionSupport !== undefined ? span.rightTorsionSupport !== false : span.rightLtbSupport !== false;
    const lw = si === 0 && span.leftWarpingSupport === true;
    const rw = si === n - 1 && span.rightWarpingSupport === true;
    rows += `<tr><td colspan="5"><strong>Span ${si + 1}</strong></td></tr>`;
    rows += `<tr><td>Length (m)</td><td colspan="4">${escapeHtml(span.length)}</td></tr>`;
    rows += `<tr><td>Left boundary</td><td colspan="4">${escapeHtml(span.leftBoundary)}</td></tr>`;
    rows += `<tr><td>Right boundary</td><td colspan="4">${escapeHtml(span.rightBoundary)}</td></tr>`;
    rows += `<tr><td>Left torsion support</td><td colspan="4">${lt ? 'Yes' : 'No'}</td></tr>`;
    rows += `<tr><td>Right torsion support</td><td colspan="4">${rt ? 'Yes' : 'No'}</td></tr>`;
    if (si === 0) {
      rows += `<tr><td>Left warping support (end)</td><td colspan="4">${lw ? 'Yes' : 'No'}</td></tr>`;
    }
    if (si === n - 1) {
      rows += `<tr><td>Right warping support (end)</td><td colspan="4">${rw ? 'Yes' : 'No'}</td></tr>`;
    }
    if (span.leftBoundary === 'spring') {
      rows += `<tr><td>Left spring My (kN·m/rad)</td><td colspan="4">${escapeHtml(span.leftSpringCoefficient)}</td></tr>`;
      rows += `<tr><td>Left spring axial / torsion / warping</td><td colspan="4">${escapeHtml([span.leftSpringAxial, span.leftSpringTorsion, span.leftSpringWarping].filter(Boolean).join(' / ') || '—')}</td></tr>`;
    }
    if (span.rightBoundary === 'spring') {
      rows += `<tr><td>Right spring My (kN·m/rad)</td><td colspan="4">${escapeHtml(span.rightSpringCoefficient)}</td></tr>`;
      rows += `<tr><td>Right spring axial / torsion / warping</td><td colspan="4">${escapeHtml([span.rightSpringAxial, span.rightSpringTorsion, span.rightSpringWarping].filter(Boolean).join(' / ') || '—')}</td></tr>`;
    }
    const loads = span.loads || [];
    if (loads.length === 0) {
      rows += `<tr><td>Loads</td><td colspan="4">None</td></tr>`;
    } else {
      loads.forEach((load, li) => {
        rows += `<tr><td>Load ${li + 1}</td><td colspan="4">${escapeHtml(formatLoadRowDescription(load))}</td></tr>`;
      });
    }
  });
  rows += `<tr><td>Loads basis</td><td colspan="4">${escapeHtml(ao.loadsBasis === 'characteristic' ? 'characteristic' : 'design')}</td></tr>`;
  rows += `<tr><td>Include self-weight</td><td colspan="4">${ao.selfWeight ? 'Yes' : 'No'}</td></tr>`;
  const naCountry = ao.eurocodeNationalAnnex === 'nl' ? 'Netherlands' : 'Finland';
  rows += `<tr><td>Eurocode national annex</td><td colspan="4">${escapeHtml(naCountry)}</td></tr>`;
  const naBundle = designChecks?.eurocode_na || designChecks?.national_annex_finland;
  const ndpRows = naBundle?.na_ndp_print_rows;
  if (naCountry === 'Finland' && Array.isArray(ndpRows) && ndpRows.length) {
    rows += `<tr><td colspan="5" class="na-ndp-subhead">${escapeHtml(
      'NA — Finland: nationally determined parameters and other NA choices used in this app (after the value: contrast vs EN recommended or generic EN text where applicable).',
    )}</td></tr>`;
    for (const row of ndpRows) {
      const p = row.parameter != null ? String(row.parameter) : '';
      const v = row.value_used != null ? String(row.value_used) : '';
      const n = row.en_note != null ? String(row.en_note) : '';
      rows += `<tr><td>${escapeHtml(p)}</td><td colspan="4">${escapeHtml(v)} — ${escapeHtml(n)}</td></tr>`;
    }
  }
  return `<h3>Geometry &amp; loads</h3><table class="inputs"><tbody>${rows}</tbody></table>`;
}

function buildSteelInputsHtml(ao, selectedSection, selectedType, results) {
  const ubForm = (ao.unbracedLengthM ?? '').toString().trim();
  const ltbForm = (ao.ltbEffectiveLengthM ?? '').toString().trim();
  const ubCell =
    ubForm !== ''
      ? escapeHtml(ubForm)
      : escapeHtml('Not set — inferred from M diagram (LTB nodes + M=0 inflections; merged spans where LTB is off)');
  const ltbCell =
    ltbForm !== ''
      ? escapeHtml(ltbForm)
      : escapeHtml('Not set — follows unbraced / inferred per span (semicolons = per span)');
  const secName = selectedSection?.name || selectedSection?.section_name || String(selectedSection?.id || '—');
  let r = `<h3>Steel (EC3) options</h3><table class="inputs"><tbody>`;
  r += `<tr><td>Section filter</td><td>${escapeHtml(selectedType || '—')}</td></tr>`;
  r += `<tr><td>Cross-section</td><td>${escapeHtml(secName)}</td></tr>`;
  r += `<tr><td>Steel grade</td><td>${escapeHtml(ao.steelGrade)}</td></tr>`;
  r += `<tr><td>E override (GPa)</td><td>${escapeHtml(ao.steelEOverrideGpa || '—')}</td></tr>`;
  r += `<tr><td>Unbraced length (m)</td><td>${ubCell}</td></tr>`;
  r += `<tr><td>LTB effective length (m)</td><td>${ltbCell}</td></tr>`;
  r += `<tr><td>Deflection limit (span/L)</td><td>${escapeHtml(ao.deflectionLimitRatio)}</td></tr>`;
  r += `<tr><td>ψ₂ (SLS)</td><td>${escapeHtml(ao.psi2)}</td></tr>`;
  r += `<tr><td>EC3 design checks</td><td>${ao.enableEc3Checks ? 'Enabled' : 'Disabled'}</td></tr>`;
  const meta = results?.analysis_meta || {};
  const ltbNodes = meta.ltb_node_support;
  if (Array.isArray(ltbNodes) && ltbNodes.length) {
    const off = ltbNodes
      .map((v, i) => (v === false ? `node ${i + 1}` : null))
      .filter(Boolean);
    if (off.length) {
      r += `<tr><td>LTB restraint unchecked at</td><td>${escapeHtml(off.join(', '))}</td></tr>`;
    }
  }
  if (meta.ltb_length_used_sagging_m != null) {
    const is = meta.ltb_critical_span_sag ?? 0;
    const c1p =
      meta.ltb_c1_per_span && meta.ltb_c1_per_span[is] != null
        ? meta.ltb_c1_per_span[is].c1
        : meta.ltb_c1;
    r += `<tr><td>LTB L used (sagging)</td><td>${escapeHtml(String(meta.ltb_length_used_sagging_m))} m at span ${is + 1}, c₁≈${escapeHtml(String(c1p))}</td></tr>`;
  }
  if (meta.ltb_length_used_hogging_m != null) {
    const ih = meta.ltb_critical_span_hog ?? 0;
    const c1h =
      meta.ltb_c1_per_span && meta.ltb_c1_per_span[ih] != null
        ? meta.ltb_c1_per_span[ih].c1
        : meta.ltb_c1;
    r += `<tr><td>LTB L used (hogging)</td><td>${escapeHtml(String(meta.ltb_length_used_hogging_m))} m at span ${ih + 1}, c₁≈${escapeHtml(String(c1h))}</td></tr>`;
  }
  r += `</tbody></table>`;
  let c1Detail = '';
  if (meta.ltb_c1 != null || (meta.ltb_c1_per_span && meta.ltb_c1_per_span.length)) {
    let c1Line =
      meta.ltb_c1 != null ? `Governing c₁ ≈ ${meta.ltb_c1} (min. over spans, from end moments on the M diagram).` : '';
    if (meta.ltb_c1_per_span && meta.ltb_c1_per_span.length) {
      const parts = meta.ltb_c1_per_span.map((d, i) => {
        let s = `span ${i + 1}: c₁=${d.c1} (L=${d.span_m} m)`;
        if (d.merged_span_indices && d.merged_span_indices.length > 1) {
          s += ` [LTB segment: spans ${d.merged_span_indices.map((j) => j + 1).join(', ')}]`;
        }
        return s;
      });
      c1Line += (c1Line ? ' ' : '') + parts.join('; ') + '.';
    }
    c1Detail = c1Line.trim();
  }
  r += '<p class="muted">';
  if (c1Detail) r += `${escapeHtml(c1Detail)} `;
  r +=
    'When lengths are blank, unbraced L is inferred within each merged LTB segment (between supports with “LTB” checked) using M=0 inflections; sagging and hogging use L and c₁ at the span of maximum positive and maximum negative moment respectively.</p>';
  return r;
}

function buildConcreteInputsHtml(ao, selectedSection) {
  const secName = selectedSection?.name || selectedSection?.section_name || String(selectedSection?.id || '—');
  let r = `<h3>Concrete (EC2) options</h3><table class="inputs"><tbody>`;
  r += `<tr><td>Cross-section preset</td><td>${escapeHtml(secName)}</td></tr>`;
  r += `<tr><td>Concrete grade</td><td>${escapeHtml(ao.concreteGrade)}</td></tr>`;
  r += `<tr><td>Reinforcement grade</td><td>${escapeHtml(ao.concreteSteelGrade)}</td></tr>`;
  r += `<tr><td>Section shape</td><td>${escapeHtml(ao.concreteSectionShape)}</td></tr>`;
  r += `<tr><td>Width b (m)</td><td>${escapeHtml(ao.concreteWidthM)}</td></tr>`;
  r += `<tr><td>Height h (m)</td><td>${escapeHtml(ao.concreteHeightM)}</td></tr>`;
  if (ao.concreteSectionShape === 'inverted_t') {
    r += `<tr><td>T flange width b_f (m)</td><td>${escapeHtml(ao.invertedTbfM)}</td></tr>`;
    r += `<tr><td>T flange thickness h_f (m)</td><td>${escapeHtml(ao.invertedThfM)}</td></tr>`;
    r += `<tr><td>T web width b_w (m)</td><td>${escapeHtml(ao.invertedTbwM)}</td></tr>`;
    r += `<tr><td>T web height h_w (m)</td><td>${escapeHtml(ao.invertedThwM)}</td></tr>`;
  }
  r += `<tr><td>Cover (m)</td><td>${escapeHtml(ao.concreteCoverM)}</td></tr>`;
  r += `<tr><td>Bar Ø (mm)</td><td>${escapeHtml(ao.concreteBarDiamMm)}</td></tr>`;
  r += `<tr><td>Bar count</td><td>${escapeHtml(ao.concreteBarCount)}</td></tr>`;
  r += `<tr><td>Auto rebar</td><td>${ao.concreteAutoRebar ? 'Yes' : 'No'}</td></tr>`;
  r += `<tr><td>Exposure class</td><td>${escapeHtml(ao.exposureClass)}</td></tr>`;
  r += `<tr><td>Analysis type</td><td>${escapeHtml(ao.analysisType)}</td></tr>`;
  r += `<tr><td>ψ₂ (quasi-permanent)</td><td>${escapeHtml(ao.psi2)}</td></tr>`;
  r += `<tr><td>EC2 design checks</td><td>${ao.enableEc2Checks ? 'Enabled' : 'Disabled'}</td></tr>`;
  r += `<tr><td>Prestress type</td><td>${escapeHtml(ao.concretePrestressType || 'none')}</td></tr>`;
  if (ao.concretePrestressType && ao.concretePrestressType !== 'none') {
    r += `<tr><td>Tendon preset</td><td>${escapeHtml(String(ao.prestressTendonPreset || '—'))}</td></tr>`;
    r += `<tr><td>Tendons (n)</td><td>${escapeHtml(String(ao.prestressNTendons ?? 0))}</td></tr>`;
    r += `<tr><td>σ_pe (MPa)</td><td>${escapeHtml(String(ao.prestressTensionMpa ?? '—'))}</td></tr>`;
    r += `<tr><td>Tendon cover (mm)</td><td>${escapeHtml(String(ao.prestressTendonCoverMm ?? '—'))}</td></tr>`;
  }
  r += `</tbody></table>`;
  return r;
}

function buildTimberInputsHtml(ao, selectedSection) {
  const secName = selectedSection?.name || selectedSection?.section_name || String(selectedSection?.id || '—');
  let r = `<h3>Timber (EC5) options</h3><table class="inputs"><tbody>`;
  r += `<tr><td>Cross-section</td><td>${escapeHtml(secName)}</td></tr>`;
  r += `<tr><td>Strength class</td><td>${escapeHtml(ao.timberStrengthClass)}</td></tr>`;
  r += `<tr><td>Service class</td><td>${escapeHtml(ao.timberServiceClass)}</td></tr>`;
  r += `<tr><td>Load duration (EC5)</td><td>${escapeHtml(ao.loadDurationClass)}</td></tr>`;
  r += `<tr><td>LTB effective length (m)</td><td>${escapeHtml(ao.ltbEffectiveLengthM || '— (longest span)')}</td></tr>`;
  r += `<tr><td>Deflection limit (span/L)</td><td>${escapeHtml(ao.deflectionLimitRatio)}</td></tr>`;
  r += `<tr><td>ψ₂</td><td>${escapeHtml(ao.psi2)}</td></tr>`;
  r += `<tr><td>EC5 design checks</td><td>${ao.enableEc5Checks ? 'Enabled' : 'Disabled'}</td></tr>`;
  r += `<tr><td>Timber width (m)</td><td>${escapeHtml(ao.timberWidthM)}</td></tr>`;
  r += `<tr><td>Timber height (m)</td><td>${escapeHtml(ao.timberHeightM)}</td></tr>`;
  r += `</tbody></table>`;
  return r;
}

function buildInputSummaryHtml(spans, advancedOptions, materialFamily, selectedSection, selectedType, results) {
  const ao = advancedOptions || {};
  let mat = '';
  if (materialFamily === 'steel') mat = buildSteelInputsHtml(ao, selectedSection, selectedType, results);
  else if (materialFamily === 'concrete') mat = buildConcreteInputsHtml(ao, selectedSection);
  else if (materialFamily === 'wood') mat = buildTimberInputsHtml(ao, selectedSection);
  else mat = `<p class="muted">Material: ${escapeHtml(materialFamily)}</p>`;
  return `<section class="block"><h2>2. Input summary</h2>${buildCommonInputsTable(spans, ao, results?.design_checks)}${mat}</section>`;
}

function buildAnalysisMetaHtml(meta) {
  if (!meta) return '';
  let s = `Loads: ${meta.loads_basis === 'characteristic' ? 'characteristic (ULS factored M/V; deflection quasi-permanent)' : 'direct (as entered)'}`;
  if (meta.ltb_c1 != null) s += ` · LTB c₁ ≈ ${meta.ltb_c1}`;
  if (meta.M_Ed_qp_kNm != null) s += ` · |M| quasi-permanent ≈ ${meta.M_Ed_qp_kNm} kNm`;
  if (meta.moment_envelope) {
    s += ` · M_Ed,sag≈${meta.moment_envelope.M_Ed_sag_kNm} kNm (V_Ed,sag≈${meta.moment_envelope.V_Ed_sag_kN} kN), M_Ed,hog≈${meta.moment_envelope.M_Ed_hog_kNm} kNm (V_Ed,hog≈${meta.moment_envelope.V_Ed_hog_kN} kN)`;
  }
  let html = `<p class="meta">${escapeHtml(s)}</p>`;
  if (meta.ltb_elastic_twist_scope_ok === false && meta.ltb_twist_scope_note) {
    html += `<p class="na-note">${escapeHtml(String(meta.ltb_twist_scope_note))}</p>`;
  }
  return html;
}

function buildDiagramsHtml(beamSvgHtml, chartImages) {
  let html = '<section class="block"><h2>3. Diagrams</h2>';
  html += '<h3>Beam model</h3>';
  if (beamSvgHtml) {
    html += `<div class="svg-wrap">${beamSvgHtml}</div>`;
  } else {
    html += '<p class="muted">Beam visualization not available.</p>';
  }
  const order = [
    ['moment', 'Bending moment'],
    ['shear', 'Shear force'],
    ['deflection', 'Deflection'],
  ];
  for (const [key, title] of order) {
    const src = chartImages && chartImages[key];
    html += `<h3>${escapeHtml(title)}</h3>`;
    if (src) {
      html += `<div class="chart-wrap"><img src="${src}" alt="${escapeHtml(title)}" /></div>`;
    } else {
      html += '<p class="muted">Chart not available.</p>';
    }
  }
  html += '</section>';
  return html;
}

function formatTableCell(num, decimals = 3) {
  if (num === null || num === undefined || Number.isNaN(Number(num))) return 'N/A';
  return Number(num).toFixed(decimals);
}

function buildResultsTablesHtml(results) {
  let html = '<section class="block"><h2>4. Tables</h2>';
  const nodal = results?.nodal_displacements;
  if (nodal && nodal.length > 0) {
    html += '<h3>Nodal displacements</h3><table class="data"><thead><tr><th>Node ID</th><th>Position (m)</th><th>Vertical disp. (mm)</th><th>Rotation (mrad)</th></tr></thead><tbody>';
    nodal.forEach((n) => {
      html += `<tr><td>${escapeHtml(n.node_id)}</td><td class="num">${formatTableCell(n.x)}</td><td class="num">${formatTableCell(
        n.vertical_displacement,
      )}</td><td class="num">${formatTableCell(n.rotation, 5)}</td></tr>`;
    });
    html += '</tbody></table>';
  }
  const reactions = results?.reactions;
  if (reactions && reactions.length > 0) {
    html += '<h3>Support reactions</h3><table class="data"><thead><tr><th>Node ID</th><th>Position (m)</th><th>Type</th><th>Value</th><th>Unit</th></tr></thead><tbody>';
    reactions.forEach((r) => {
      const unit = r.unit ?? (String(r.type || '').startsWith('Force') ? 'kN' : 'kNm');
      const xm = r.x_m != null ? formatTableCell(r.x_m) : '—';
      html += `<tr><td>${escapeHtml(r.node_id)}</td><td class="num">${xm}</td><td>${escapeHtml(r.type)}</td><td class="num">${formatTableCell(
        r.value,
        3,
      )}</td><td>${escapeHtml(unit)}</td></tr>`;
    });
    html += '</tbody></table>';
  }
  if ((!nodal || !nodal.length) && (!reactions || !reactions.length)) {
    html += '<p class="muted">No tabular results.</p>';
  }
  html += '</section>';
  return html;
}

function buildReportStyles() {
  return `
  @page { margin: 12mm; size: A4; }
  body { font-family: system-ui, Segoe UI, Roboto, Arial, sans-serif; font-size: 10pt; color: #111; line-height: 1.35; }
  h1 { font-size: 16pt; margin: 0 0 8px; }
  h2 { font-size: 12pt; margin: 16px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; page-break-after: avoid; }
  h3 { font-size: 10.5pt; margin: 12px 0 6px; page-break-after: avoid; }
  .muted { color: #666; font-size: 9pt; }
  .meta { font-size: 9pt; color: #444; margin: 8px 0; }
  .na-note { font-size: 9pt; color: #444; margin-bottom: 8px; }
  .err { color: #b00020; font-weight: 600; }
  .block { page-break-inside: avoid; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0 12px; font-size: 9pt; }
  table.checks th, table.checks td, table.inputs td, table.data th, table.data td { border: 1px solid #ccc; padding: 4px 6px; vertical-align: top; }
  table.inputs td:first-child { width: 28%; font-weight: 600; background: #f5f5f5; }
  table.inputs td.na-ndp-subhead { font-weight: 600; background: #f0f4f8; font-size: 8.5pt; }
  table.data th { background: #eee; text-align: left; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .small { font-size: 8.5pt; max-width: 120px; }
  .extra { font-size: 8.5pt; }
  .hint { font-size: 8pt; color: #555; }
  .util { text-align: right; font-weight: 600; }
  .util-low { background: #e8f5e9; }
  .util-mid { background: #fff8e1; }
  .util-high { background: #ffebee; }
  .status { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8pt; font-weight: 600; }
  .status.ok { background: #c8e6c9; color: #1b5e20; }
  .status.fail { background: #ffcdd2; color: #b71c1c; }
  .status.warn { background: #ffe082; color: #5d4037; }
  .svg-wrap { border: 1px solid #ddd; padding: 8px; max-width: 100%; overflow: auto; page-break-inside: avoid; }
  .svg-wrap svg { max-width: 100%; height: auto; display: block; }
  .chart-wrap { page-break-inside: avoid; margin: 8px 0; text-align: center; }
  .chart-wrap img { max-width: 100%; height: auto; border: 1px solid #eee; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
`;
}

function buildFullReportHtml(data) {
  const {
    results,
    spans,
    advancedOptions,
    materialFamily,
    selectedSection,
    selectedType,
    beamSvgHtml,
    chartImages,
    generatedAt,
  } = data;
  const title = 'Continuous beam — calculation report';
  const matLabel =
    materialFamily === 'steel' ? 'Steel' : materialFamily === 'concrete' ? 'Concrete' : materialFamily === 'wood' ? 'Timber' : materialFamily;
  const header = `<header><h1>${escapeHtml(title)}</h1><p class="muted">Generated: ${escapeHtml(generatedAt || new Date().toISOString())} · Material: ${escapeHtml(
    matLabel,
  )} · Section: ${escapeHtml(sectionTitle(selectedSection?.name || selectedSection?.section_name))}</p></header>`;
  const meta = buildAnalysisMetaHtml(results?.analysis_meta);
  const dc = buildDesignChecksHtml(results?.design_checks);
  const inputs = buildInputSummaryHtml(spans, advancedOptions, materialFamily, selectedSection, selectedType, results);
  const diagrams = buildDiagramsHtml(beamSvgHtml, chartImages);
  const tables = buildResultsTablesHtml(results);
  const styles = buildReportStyles();
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtml(title)}</title><style>${styles}</style></head><body>${header}${meta}${dc}${inputs}${diagrams}${tables}</body></html>`;
}

/**
 * Opens a new tab with the report and triggers the print dialog.
 * @param {object} reportData
 * @param {object} reportData.results — API results object
 * @param {array} reportData.spans
 * @param {object} reportData.advancedOptions
 * @param {string} reportData.materialFamily
 * @param {object} [reportData.selectedSection]
 * @param {string} [reportData.selectedType]
 * @param {string} [reportData.beamSvgHtml]
 * @param {{ moment?: string, shear?: string, deflection?: string }} [reportData.chartImages] — data URLs
 * @param {string} [reportData.generatedAt]
 */
export function openPrintReport(reportData) {
  const html = buildFullReportHtml({
    ...reportData,
    generatedAt: reportData.generatedAt || new Date().toLocaleString(),
  });
  // Blob URL + navigation is reliable across browsers. Writing to a blank tab opened with
  // `noopener` often leaves an empty document (opener cannot populate it).
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    URL.revokeObjectURL(url);
    window.alert('Pop-up blocked. Allow pop-ups for this site to open the print report.');
    return;
  }
  let printCalled = false;
  const runPrint = () => {
    if (printCalled) return;
    printCalled = true;
    try {
      w.focus();
      w.print();
    } catch (e) {
      console.error(e);
    }
  };
  // Wait for the blob document to finish loading so charts/SVG render before print.
  if (w.document.readyState === 'complete') {
    setTimeout(runPrint, 250);
  } else {
    w.addEventListener('load', () => setTimeout(runPrint, 250), { once: true });
    setTimeout(runPrint, 2000);
  }
  // Free the object URL after the tab is gone (revoking earlier can blank the page).
  w.addEventListener(
    'beforeunload',
    () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* ignore */
      }
    },
    { once: true },
  );
}

export default openPrintReport;
